import type {LinkedLiterary} from '../../schemaDefinition/types';
import type {BulkUpdate, Collection} from '../types';
import type {Schemas} from '../dto.generated';
import {mapBulkUpdateWorkField} from './mapBulkUpdateWorkField';

export const mapBulkUpdateFieldAndAction = (
  dtoField: Schemas.BulkUpdateStatusResponse['field'],
  dtoAction: Schemas.BulkUpdateStatusResponse['action'],
  collections?: Collection[],
): Pick<
  BulkUpdate,
  | 'bulkUpdateField'
  | 'bulkUpdateAction'
  | 'currentValues'
  | 'newValues'
  | 'addToFirstPosition'
  | 'type'
> => {
  const addActionDto = dtoAction as Schemas.BulkUpdateActionAdd;
  const removeActionDto = dtoAction as Schemas.BulkUpdateActionRemove;
  const replaceActionDto = dtoAction as Schemas.BulkUpdateActionReplace;

  const field = mapBulkUpdateWorkField(dtoField);

  switch (dtoAction.action) {
    case 'ADD': {
      return {
        bulkUpdateAction: dtoAction.action,
        bulkUpdateField: field.bulkUpdateField,
        type: field.type,
        addToFirstPosition: addActionDto.addToFirstPosition,
        newValues: asValueOrLinkedLiterary(addActionDto.addValues, collections),
      };
    }
    case 'REMOVE': {
      return {
        bulkUpdateAction: dtoAction.action,
        bulkUpdateField: field.bulkUpdateField,
        type: field.type,
        currentValues: asValueOrLinkedLiterary(
          removeActionDto.removeValues,
          collections,
        ),
      };
    }
    case 'REPLACE': {
      return {
        bulkUpdateAction: dtoAction.action,
        bulkUpdateField: field.bulkUpdateField,
        type: field.type,
        currentValues: asValueOrLinkedLiterary(
          replaceActionDto.currentValue,
          collections,
        ),
        newValues: asValueOrLinkedLiterary(
          replaceActionDto.updatedValues,
          collections,
        ),
        addToFirstPosition: replaceActionDto.addToFirstPosition,
      };
    }
    default:
      throw new Error(
        `mapBulkUpdateFieldAndAction: Expected action: "ADD", "REMOVE" or "REPLACE" got "${dtoAction.action}"`,
      );
  }
};

/**
 * Converts a value or an array of values to either an array of strings or an array of LinkedLiterary objects.
 */
const asValueOrLinkedLiterary = (
  value: string | string[],
  collections?: Collection[],
): string[] | LinkedLiterary[] => {
  const values = Array.isArray(value) ? value : [value];

  return values.map(v => idOrLinkedLiterary(v, collections)) as
    | string[]
    | LinkedLiterary[];
};

/**
 * Converts a value to LinkedLiterary object if matching id is present in collections.
 */
const idOrLinkedLiterary = (
  value: string,
  collections?: Collection[],
): LinkedLiterary | string => {
  const collection = collections?.find(c => c.id === value);

  if (collection) {
    return {
      link: {
        entityId: collection.id,
        linkStatus: 'verified',
        name: collection.titles[0].value ?? '',
        type: collection.collectionType,
      },
      role: `partOf.${collection.collectionType}`,
    };
  }

  return value;
};
